import { isDefined } from "@clipboard-health/util-ts";
import { CssBaseline, Stack, ThemeProvider } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { useGetWorkplace } from "@src/appV2/Facilities/api/useGetWorkplace";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { PageHeaderWrapper, Text } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { AllRolesFilterProvider } from "./hooks/useAllRolesFilter";
import { ShiftBlockFilter } from "./ShiftBlockFilter";
import { ShiftBlocks } from "./ShiftBlocks";

interface ShiftBlockPageProps {
  agent: Worker;
  facilityId?: string;
  orientationBlockQualification?: string;
}

export function ShiftBlockPage(props: ShiftBlockPageProps) {
  const { agent, facilityId, orientationBlockQualification } = props;
  const location = useLocation();

  const { data: workplace } = useGetWorkplace(
    { workplaceId: facilityId },
    { enabled: isDefined(facilityId) }
  );

  const facilityName = workplace?.data?.attributes?.name;

  const isOrientationBlock = isDefined(orientationBlockQualification);
  const showHeader = isOrientationBlock || agent.licensesData.length > 1;

  // Read "allRoles" query param to initialize filter state (e.g., from callout navigation)
  const initialAllRolesSelected = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("allRoles") === "true";
  }, [location.search]);

  const headerTitle = isOrientationBlock ? (
    <Stack alignItems="center">
      {isDefined(facilityName) && <Text bold>{facilityName}</Text>}
      <Text sx={{ color: (theme) => theme.palette.text.secondary }}>
        {orientationBlockQualification} Orientation Blocks
      </Text>
    </Stack>
  ) : (
    <ShiftBlockFilter worker={agent} />
  );

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <AllRolesFilterProvider initialValue={initialAllRolesSelected}>
        <PageWrapper>
          {showHeader && (
            <PageHeaderWrapper>
              <PageHeaderTopRow fallbackGoBackPath={RootPaths.APP_V2_HOME} title={headerTitle} />
            </PageHeaderWrapper>
          )}

          <PageContent>
            <ShiftBlocks
              worker={agent}
              facilityId={facilityId}
              orientationBlockQualification={orientationBlockQualification}
            />
          </PageContent>
        </PageWrapper>
      </AllRolesFilterProvider>
    </ThemeProvider>
  );
}
